import { useCallback, useEffect, useRef, useState } from "react";

export interface IRevealGateOptions {
  assetCount?: number;
  preload?: string[];
  isDataReady?: boolean;
  timeoutMs?: number;
}

export interface IRevealGate {
  isReady: boolean;
  onAssetLoad: () => void;
}

export default function useRevealGate(options?: IRevealGateOptions): IRevealGate {
  const { assetCount = 0, preload, isDataReady = true, timeoutMs = 1500 } = options ?? {};

  const [loaded, setLoaded] = useState(0);
  const [timedOut, setTimedOut] = useState(false);
  const hasBeenReadyRef = useRef(false);

  const onAssetLoad = useCallback(() => {
    setLoaded((count) => count + 1);
  }, []);

  const preloadKey = preload ? preload.join("|") : "";

  useEffect(() => {
    if (!preload || preload.length === 0) return;
    const images: HTMLImageElement[] = [];
    preload.forEach((src) => {
      const image = new window.Image();
      image.onload = onAssetLoad;
      image.onerror = onAssetLoad;
      image.src = src;
      images.push(image);
    });
    return () => {
      images.forEach((image) => {
        image.onload = null;
        image.onerror = null;
      });
    };
  }, [preloadKey, onAssetLoad]);

  useEffect(() => {
    if (!isDataReady) return;
    const timer = window.setTimeout(() => {
      setTimedOut(true);
    }, timeoutMs);
    return () => {
      window.clearTimeout(timer);
    };
  }, [isDataReady, timeoutMs]);

  const preloadCount = preload?.length ?? 0;
  const isCurrentlyReady = isDataReady && (loaded >= assetCount + preloadCount || timedOut);
  if (isCurrentlyReady) {
    hasBeenReadyRef.current = true;
  }

  const isReady = hasBeenReadyRef.current || isCurrentlyReady;

  return { isReady, onAssetLoad };
}
